import React, { createContext, useContext, useReducer, Dispatch } from 'react';

// Define the shape of the sidebar state
interface SidebarState {
  isOpen: boolean;
  isCollapsed: boolean;
  activeItemId: string | null;
}

// Define action types
type SidebarAction = 
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'TOGGLE_COLLAPSE' }
  | { type: 'SET_ACTIVE_ITEM', payload: string }
  | { type: 'RESET_SIDEBAR' };

// Initial state
const initialState: SidebarState = {
  isOpen: true,
  isCollapsed: false,
  activeItemId: 'home'
};

// Reducer function with explicit typing
function sidebarReducer(state: SidebarState, action: SidebarAction): SidebarState {
  switch (action.type) {
    case 'TOGGLE_SIDEBAR':
      return { ...state, isOpen: !state.isOpen };
    
    case 'TOGGLE_COLLAPSE':
      return { ...state, isCollapsed: !state.isCollapsed };
    
    case 'SET_ACTIVE_ITEM':
      return { ...state, activeItemId: action.payload };
    
    case 'RESET_SIDEBAR':
      return initialState;
    
    default:
      return state;
  }
}

// Create context with explicit typing
const SidebarContext = createContext<{
  state: SidebarState;
  dispatch: Dispatch<SidebarAction>;
} | undefined>(undefined);

// Provider component
export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(sidebarReducer, initialState);

  return (
    <SidebarContext.Provider value={{ state, dispatch }}>
      {children}
    </SidebarContext.Provider>
  );
};

// Custom hook for using sidebar context
export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

// Utility functions for common sidebar actions
export function updateSidebarForPath(path: string, dispatch: Dispatch<SidebarAction>) {
  if (path === '/') {
    dispatch({ type: 'SET_ACTIVE_ITEM', payload: 'home' });
  } else if (path.startsWith('/sales')) {
    dispatch({ type: 'SET_ACTIVE_ITEM', payload: 'new-sale' });
  } else if (path === '/profile') {
    dispatch({ type: 'SET_ACTIVE_ITEM', payload: 'profile' });
  } else if (path === '/help') {
    dispatch({ type: 'SET_ACTIVE_ITEM', payload: 'help' });
  }
}

export default SidebarContext;